/**
 * Компонент ручной отметки сотрудников - Enterprise Design v2.0
 * QRIUM Recvisit
 */
import { useState, useEffect, useRef, useCallback } from 'react'
import { EventsRepo } from '../offline/eventsRepo'
import { EmployeesRepo, type EmployeeFullData } from '../offline/employeesRepo'
import { EmployeesService } from '../services/employeesService'
import { CryptoService } from '../services/cryptoService'
import { useDeviceStore } from '../store/deviceStore'
import { LogsRepo } from '../offline/logsRepo'
import { toMSKISOString } from '../utils/timezone'

export function ManualCheckForm() {
  const deviceStore = useDeviceStore()
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeFullData | null>(null)
  const [eventType, setEventType] = useState<'check_in' | 'check_out'>('check_in')
  const [customTime, setCustomTime] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [employees, setEmployees] = useState<EmployeeFullData[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const loadEmployees = useCallback(async () => {
    try {
      const allEmployees = await EmployeesRepo.getAllFullData()
      const activeEmployees = allEmployees.filter((emp) => emp.is_active)
      activeEmployees.sort((a, b) => a.full_name.localeCompare(b.full_name, 'ru'))
      setEmployees(activeEmployees)
    } catch (error) {
      console.error('Failed to load employees:', error)
    }
  }, [])

  useEffect(() => {
    loadEmployees()
  }, [loadEmployees])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const filteredEmployees = employees.filter((emp) => {
    const query = searchQuery.toLowerCase()
    return (
      emp.full_name.toLowerCase().includes(query) ||
      emp.external_id.toLowerCase().includes(query) ||
      emp.employee_id.toLowerCase().includes(query)
    )
  })

  const handleEmployeeSelect = (employee: EmployeeFullData) => {
    setSelectedEmployee(employee)
    setSearchQuery('')
    setIsDropdownOpen(false)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)
    setIsLoading(true)

    try {
      if (!deviceStore.siteId || !deviceStore.deviceId) {
        throw new Error('Устройство не зарегистрировано')
      }

      if (!selectedEmployee) {
        throw new Error('Выберите сотрудника из списка')
      }

      const employeeId = selectedEmployee.employee_id

      let happenedAt: Date
      if (customTime) {
        const selectedTime = new Date(customTime)
        const now = new Date()
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        const minTime = new Date(today.getTime() - 60 * 60 * 1000)
        const maxTime = new Date(today.getTime() + 24 * 60 * 60 * 1000 + 60 * 60 * 1000)
        
        if (selectedTime < minTime || selectedTime > maxTime) {
          throw new Error('Время должно быть в пределах сегодня ±1 час')
        }
        happenedAt = selectedTime
      } else {
        happenedAt = new Date()
      }

      if (navigator.onLine && deviceStore.siteId) {
        try {
          const publicKey = await EmployeesService.getPublicKey(employeeId, deviceStore.siteId)
          if (!publicKey) {
            throw new Error(`Сотрудник с ID ${employeeId} не найден`)
          }
        } catch (error) {
          throw new Error(error instanceof Error ? error.message : 'Сотрудник не найден')
        }
      }

      const happenedAtISO = toMSKISOString(happenedAt)
      
      const payloadHash = await CryptoService.calculatePayloadHash({
        employee_id: employeeId,
        site_id: deviceStore.siteId,
        device_id: deviceStore.deviceId,
        event_type: eventType,
        happened_at: happenedAtISO,
        qr_token: '',
      })

      const eventId = await EventsRepo.save({
        employee_id: employeeId,
        site_id: deviceStore.siteId,
        device_id: deviceStore.deviceId,
        event_type: eventType,
        happened_at: happenedAtISO,
        payload_hash: payloadHash,
        source: 'manual',
      })

      await LogsRepo.save('info', 'Manual event created', {
        employee_id: employeeId,
        event_type: eventType,
        event_id: eventId,
      })

      // Немедленная синхронизация в фоне если есть подключение к интернету
      if (navigator.onLine) {
        const { SyncService } = await import('../services/syncService')
        SyncService.syncNow().catch((syncError) => {
          console.error('[ManualCheckForm] Failed to sync immediately:', syncError)
          // Не показываем ошибку пользователю - событие будет синхронизировано позже
        })
      }

      setSuccess('Событие создано успешно.')
      setSelectedEmployee(null)
      setCustomTime('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="space-y-section">
      {/* Info Card */}
      <div className="info-card">
        <div className="info-card-icon">ℹ️</div>
        <p className="text-[14px] text-[#a0a0a0] leading-relaxed flex-1">
          Создайте событие прихода или ухода вручную. Время можно установить в пределах сегодня ±1 час.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-section">
        {/* Employee Select */}
        <div ref={dropdownRef} className="focus-block">
          <label className="block text-[14px] font-semibold text-white mb-3">
            Сотрудник
          </label>
          <div className="relative">
            <button
              type="button"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="w-full flex items-center justify-between bg-[#1a1a1a] border-2 border-[#2e2e2e] rounded-xl text-left transition-all hover:border-[#3b82f6] focus:border-[#3b82f6] focus:outline-none"
              style={{height: '56px', padding: '0 20px', gap: '16px'}}
            >
              {selectedEmployee ? (
                <div className="flex-1 min-w-0" style={{paddingRight: '12px'}}>
                  <p className="text-white font-medium text-[15px] truncate">{selectedEmployee.full_name}</p>
                  <p className="text-[12px] text-[#6b6b6b] truncate" style={{marginTop: '4px'}}>{selectedEmployee.external_id}</p>
                </div>
              ) : (
                <span className="text-[#6b6b6b] text-[15px]">Выберите сотрудника...</span>
              )}
              <span className={`text-[#6b6b6b] flex-shrink-0 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} style={{fontSize: '18px'}}>▼</span>
            </button>

            {isDropdownOpen && (
              <div className="absolute z-30 w-full bg-[#1a1a1a] rounded-xl shadow-2xl shadow-black/60 overflow-hidden animate-fade-in border-2 border-[#3a3a3a]" style={{marginTop: '12px', maxHeight: '400px'}}>
                <div className="border-b border-[#2e2e2e]" style={{padding: '18px 20px'}}>
                  <input
                    type="text"
                    placeholder="Поиск по ФИО, ID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                    className="w-full bg-[#242424] border-2 border-[#3a3a3a] rounded-lg text-white text-[15px] placeholder:text-[#6b6b6b] focus:outline-none focus:border-[#3b82f6]"
                    style={{padding: '14px 18px'}}
                  />
                </div>
                <div className="overflow-y-auto" style={{maxHeight: '300px'}}>
                  {filteredEmployees.length === 0 ? (
                    <div className="text-center text-[#6b6b6b] text-[15px]" style={{padding: '40px 24px'}}>
                      {employees.length === 0 ? 'Нет сотрудников' : 'Ничего не найдено'}
                    </div>
                  ) : (
                    filteredEmployees.map((emp) => (
                      <button
                        key={emp.employee_id}
                        type="button"
                        onClick={() => handleEmployeeSelect(emp)}
                        className="w-full text-left hover:bg-[#242424] active:bg-[#2e2e2e] border-b border-[#2e2e2e] last:border-b-0 transition-colors"
                        style={{padding: '18px 24px'}}
                      >
                        <p className="text-white font-medium text-[15px]" style={{marginBottom: '6px'}}>{emp.full_name}</p>
                        <p className="text-[12px] text-[#6b6b6b]">{emp.external_id}</p>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Event Type */}
        <div className="focus-block">
          <label className="block text-[14px] font-semibold text-white mb-3">
            Тип события
          </label>
          <div className="stats-grid-2">
            <button
              type="button"
              onClick={() => setEventType('check_in')}
              disabled={isLoading}
              className={`btn btn-lg ${eventType === 'check_in' ? 'btn-success' : 'btn-secondary'}`}
            >
              <span className="text-xl">📥</span>
              <span>Приход</span>
            </button>
            <button
              type="button"
              onClick={() => setEventType('check_out')}
              disabled={isLoading}
              className={`btn btn-lg ${eventType === 'check_out' ? 'btn-danger' : 'btn-secondary'}`}
            >
              <span className="text-xl">📤</span>
              <span>Уход</span>
            </button>
          </div>
        </div>

        {/* Custom Time */}
        <div className="focus-block">
          <label className="block text-[14px] font-semibold text-white mb-3">
            Время <span className="font-normal text-[#6b6b6b]">(опционально)</span>
          </label>
          <input
            type="datetime-local"
            value={customTime}
            onChange={(e) => setCustomTime(e.target.value)}
            disabled={isLoading}
            className="w-full bg-[#2a2a2a] border-2 border-[#3a3a3a] rounded-xl text-white text-[14px] font-medium cursor-pointer transition-all hover:border-[#3b82f6] focus:border-[#3b82f6] focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              padding: '12px 16px',
              minHeight: '48px',
              colorScheme: 'dark'
            }}
          />
          <p className="text-[11px] text-[#6b6b6b] mt-2 leading-tight">
            Если не указано, используется текущее время
          </p>
        </div>

        {/* Messages */}
        {error && (
          <div className="flex items-start gap-3 p-4 bg-[#ef4444]/10 border border-[#ef4444]/25 rounded-xl animate-fade-in">
            <span className="text-lg flex-shrink-0">❌</span>
            <p className="text-[#ef4444] text-[14px] leading-relaxed">{error}</p>
          </div>
        )}

        {success && (
          <div className="flex items-start gap-3 p-4 bg-[#22c55e]/10 border border-[#22c55e]/25 rounded-xl animate-fade-in">
            <span className="text-lg flex-shrink-0">✅</span>
            <p className="text-[#22c55e] text-[14px] leading-relaxed">{success}</p>
          </div>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading || !selectedEmployee}
          className="btn btn-lg btn-primary btn-full"
        >
          {isLoading ? (
            <>
              <div className="loading-spinner w-5 h-5 border-2" />
              <span>Создание...</span>
            </>
          ) : (
            <span>Создать событие</span>
          )}
        </button>
      </form>
    </div>
  )
}
